import { JOBS_LISTING_SEGMENT, JOBS_LISTING_SEGMENTS } from '../../utils/constants/url-constants';
import { workModelForSegment } from '../../utils/work-model/work-model-hub';

/** Matches {@link parseUrlToIndexUiState} / {@link generateUrlFromIndexUiState} jobs URL shape. */
const JOBS_SEGMENTS = new Set(JOBS_LISTING_SEGMENTS);
const LOCATION_SEGMENTS = new Set(["location", "omrade", "område", "omr%C3%A5de", "alue"]);
// Maakunta (Finnish admin1 region) hub marker: /jobs/region/{slug}, /tyopaikat/maakunta/{slug},
// /lediga-jobb/landskap/{slug}. Routed the same as a city location segment (this file only needs
// to recognize the URL shape belongs to the index SRP page; the region-vs-city distinction is
// made downstream in utils/search-router/url-handling.ts's own parse).
const REGION_SEGMENTS = new Set(["region", "maakunta", "landskap"]);
// Current markers (url-handling JOBS_COMPANY_PATH_SEGMENT) plus the legacy ones, which
// url-rewrite-config 301s but must still route in case the rewrite layer is bypassed.
const COMPANY_SEGMENTS = new Set(["employer", "tyonantaja", "arbetsgivare", "company", "yritys", "foretag"]);
const CATEGORY_SEGMENTS = new Set(["category", "kategoria", "kategori"]);
const ROLE_SEGMENTS = new Set(["role", "ammatti", "yrke"]);
const FREELANCE_SEGMENTS = new Set(["freelance", "keikkatyo", "frilans"]);

const fileExtensionPattern = /\.(map|js|css|json|png|jpg|jpeg|gif|svg|webp|ico|woff|woff2|ttf|eot)$/i;

export type IndexRouteParams = {
  locale: string;
  jobString: string;
  areaGroup: string;
  location: string;
  company: string;
  category: string;
  role: string;
  /** Vike requires routeParams values to be strings — "true" or "" (never a boolean). */
  freelance: string;
  /** "remote" | "hybrid" | "" : the work-model marker right after the jobs segment (utils/work-model). */
  workModel: string;
};

/**
 * True when pathname should use the index SRP (+data.ts + parseUrlToIndexUiState).
 * Keeps parity with path encoding in utils/search-router/url-handling.ts.
 */
export function matchSearchIndexPath(path: string): { routeParams: IndexRouteParams } | false {
  let p = path !== "/" ? path.replace(/\/$/, "") : path;
  if (p.endsWith(".pageContext.json")) {
    p = p.slice(0, -".pageContext.json".length) || "/";
  }
  if (fileExtensionPattern.test(p)) {
    return false;
  }

  const segments = p.split("/").filter(Boolean);
  let i = 0;
  let locale = "en";

  if (segments[0] && ["en", "fi", "sv"].includes(segments[0].toLowerCase())) {
    locale = segments[0].toLowerCase();
    i = 1;
  }

  // Locale-only roots (/, /en, /fi, /sv): bare SRP home, no jobs-listing segment in the URL.
  // jobString still gets the locale's real listing segment (not '') — nothing reads this field
  // by name, but giving it the same value /{locale}/jobs would produce keeps this route's
  // routeParams shape identical to the equivalent explicit URL, rather than introducing a novel
  // empty-string state other code was never written to expect.
  if (i >= segments.length) {
    return {
      routeParams: {
        locale,
        jobString: JOBS_LISTING_SEGMENT[locale as keyof typeof JOBS_LISTING_SEGMENT],
        areaGroup: "",
        location: "",
        company: "",
        category: "",
        role: "",
        freelance: "",
        workModel: "",
      },
    };
  }

  const jobSeg = segments[i];
  if (!JOBS_SEGMENTS.has(jobSeg)) {
    return false;
  }
  i += 1;

  let areaGroup = "";
  let location = "";
  let company = "";
  let category = "";
  let role = "";
  let freelance = "";
  let workModel = "";

  // Work model (remote / hybrid) comes first and combines with everything after it:
  // /tyopaikat/etatyo, /tyopaikat/etatyo/ammatti/x, /tyopaikat/hybridityo/alue/tampere_suomi.
  if (i < segments.length) {
    const wm = workModelForSegment(segments[i]);
    if (wm) {
      workModel = wm;
      i += 1;
    }
  }

  // Bare, terminal segment only — /jobs/freelance, /tyopaikat/keikkatyo, /lediga-jobb/frilans.
  // Deliberately not combinable with location/company/category/role as a pretty path (a combo
  // like "freelance jobs in Helsinki" still works via ?jobType=Freelance&location=... query
  // params, same as any other flat facet combo) — keeps this one hub simple to reason about.
  if (
    i < segments.length &&
    i === segments.length - 1 &&
    FREELANCE_SEGMENTS.has(segments[i].toLowerCase())
  ) {
    freelance = "true";
    i += 1;
  }

  if (i < segments.length && (LOCATION_SEGMENTS.has(segments[i]) || REGION_SEGMENTS.has(segments[i]))) {
    areaGroup = segments[i];
    const locVal = segments[i + 1];
    if (!locVal || fileExtensionPattern.test(locVal)) {
      return false;
    }
    location = locVal;
    i += 2;
  }

  if (i < segments.length && COMPANY_SEGMENTS.has(segments[i].toLowerCase())) {
    const coVal = segments[i + 1];
    if (!coVal || fileExtensionPattern.test(coVal)) {
      return false;
    }
    company = coVal;
    i += 2;
  }

  if (i < segments.length && CATEGORY_SEGMENTS.has(segments[i].toLowerCase())) {
    const catVal = segments[i + 1];
    if (!catVal || fileExtensionPattern.test(catVal)) {
      return false;
    }
    category = catVal;
    i += 2;
  }

  if (i < segments.length && ROLE_SEGMENTS.has(segments[i].toLowerCase())) {
    const roleVal = segments[i + 1];
    if (!roleVal || fileExtensionPattern.test(roleVal)) {
      return false;
    }
    role = roleVal;
    i += 2;
  }

  if (i < segments.length) {
    return false;
  }

  return {
    routeParams: {
      locale,
      jobString: jobSeg,
      areaGroup,
      location,
      company,
      category,
      role,
      freelance,
      workModel,
    },
  };
}
